import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Camera, Check, X as XIcon, Download, AlertTriangle, MapPin, Truck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Trip {
  id: string; trip_code: string; status: string;
  client_name: string | null;
  pickup_city: string; pickup_location_name: string | null;
  delivery_city: string; delivery_location_name: string | null;
  container_type: string | null; container_number: string | null;
  rate_sar: string;
}

interface Doc { id: string; document_type: string; uploaded_at: string }

const NEXT_STATUS: Record<string, string> = {
  assigned: 'en_route_to_pickup',
  en_route_to_pickup: 'at_pickup',
  at_pickup: 'loaded',
  loaded: 'in_transit',
  in_transit: 'at_delivery',
  at_delivery: 'unloaded',
  unloaded: 'completed',
  returning: 'completed',
};

const REQUIRED_PHOTOS_BEFORE: Record<string, string[]> = {
  loaded:   ['pickup_photo','shipper_receipt','loaded_truck_photo'],
  unloaded: ['delivery_photo','signed_pod'],
};

export default function DriverTripActionPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [pickedDocType, setPickedDocType] = useState<string>('pickup_photo');

  const refresh = useCallback(async () => {
    if (!tripId) return;
    try {
      const r = await apiClient.get<{ trip: Trip; documents: Doc[] }>('/api/v1/flatbed/trips/' + tripId);
      setTrip(r.data.trip);
      setDocs(r.data.documents);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function uploadPhoto(file: File, docType: string) {
    if (!tripId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('documentType', docType);
      fd.append('file', file);
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 6000 }));
        fd.append('gpsLat', String(pos.coords.latitude));
        fd.append('gpsLng', String(pos.coords.longitude));
      } catch { /* ignore */ }
      const jwt = getStoredToken();
      const res = await fetch(API_BASE + '/api/v1/flatbed/trips/' + tripId + '/documents', {
        method: 'POST',
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
        body: fd,
      });
      if (!res.ok) { const t = await res.text(); window.alert('Upload failed: ' + t.slice(0, 200)); return; }
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function transition() {
    if (!trip) return;
    const next = NEXT_STATUS[trip.status];
    if (!next) return;
    setBusy(true);
    try {
      let gps: { lat?: number; lng?: number } = {};
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 6000 }));
        gps = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch { /* ignore */ }
      await apiClient.post('/api/v1/flatbed/trips/' + trip.id + '/transition', { newStatus: next, gpsLat: gps.lat, gpsLng: gps.lng });
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !trip) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'Not found'}</div>
    </div>
  );

  const next = NEXT_STATUS[trip.status];
  const requiredForNext = next ? (REQUIRED_PHOTOS_BEFORE[next] ?? []) : [];
  const haveTypes = new Set(docs.map((d) => d.document_type));
  const consignmentDoc = docs.find((d) => d.document_type === 'consignment_note');

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/flatbed/my-trips"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> My trips</Button></Link>
          <div className="text-sm font-medium font-mono">{trip.trip_code}</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-3 space-y-1">
          <div className="text-sm font-medium inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {trip.pickup_city} → {trip.delivery_city}</div>
          {trip.client_name && <div className="text-xs text-muted-foreground">{trip.client_name}</div>}
          {trip.container_type && <div className="text-xs text-muted-foreground inline-flex items-center gap-2"><Truck className="h-3 w-3" /> {trip.container_type}{trip.container_number ? ' · ' + trip.container_number : ''}</div>}
        </div>

        {consignmentDoc && (
          <a href={API_BASE + '/api/v1/flatbed/trips/' + trip.id + '/documents/' + consignmentDoc.id + '/file'} target="_blank" rel="noreferrer">
            <Button variant="outline" className="w-full gap-2"><Download className="h-4 w-4" /> Consignment note</Button>
          </a>
        )}

        <div className="rounded-lg border border-border p-4 text-center">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Current status</div>
          <div className="text-2xl font-medium mt-1">{trip.status.replace(/_/g, ' ')}</div>
          {next && <div className="text-xs text-muted-foreground mt-1">Next: {next.replace(/_/g, ' ')}</div>}
        </div>

        {requiredForNext.length > 0 && (
          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Required before {next?.replace(/_/g, ' ')}</div>
            {requiredForNext.map((p) => (
              <div key={p} className="flex items-center gap-2 text-sm">
                {haveTypes.has(p) ? <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <XIcon className="h-4 w-4 text-rose-600 dark:text-rose-400" />}
                <span className="flex-1">{p.replace(/_/g, ' ')}</span>
                {!haveTypes.has(p) && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setPickedDocType(p); fileRef.current?.click(); }}>
                    <Camera className="h-3.5 w-3.5" /> Photo
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f, pickedDocType); e.target.value=''; }} />

        {next && (
          <Button onClick={transition} disabled={busy || requiredForNext.some((r) => !haveTypes.has(r))} className="w-full gap-2 h-12 text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            Confirm {next.replace(/_/g, ' ')}
          </Button>
        )}

        {docs.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Uploaded</div>
            {docs.map((d) => (
              <div key={d.id} className="rounded-lg border border-border p-2 text-xs flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="flex-1">{d.document_type.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground">{new Date(d.uploaded_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
